import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Download, 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Eye,
  Play,
  Loader2,
  RefreshCw,
  ArrowRight,
  Trash2,
  Edit,
  GitMerge
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step indicator component (moved outside to prevent re-creation on each render)
const StepIndicator = ({ step, title, active, completed }) => (
  <div className={`flex items-center ${active ? 'text-blue-600' : completed ? 'text-green-600' : 'text-gray-400'}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
      completed ? 'bg-green-600 border-green-600 text-white' :
      active ? 'border-blue-600 text-blue-600' : 'border-gray-300'
    }`}>
      {completed ? <CheckCircle className="w-5 h-5" /> : step}
    </div>
    <span className="ml-2 font-medium">{title}</span>
  </div>
);

const ExcelWorkflow = ({ onRefresh }) => {
  const [activeStep, setActiveStep] = useState(1); // 1: Export, 2: Upload/Preview, 3: Apply
  const [isExporting, setIsExporting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Step 1: Export Excel
  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/exercises/export/excel`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'exercises_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to export Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Step 2: Upload and Preview
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx')) {
      setError('Please upload an Excel file (.xlsx)');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    setPreviewReport(null);
    setUploadedFileName(file.name);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/exercises/import/preview`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setPreviewReport(response.data);
      if (response.data.valid) {
        setActiveStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to preview Excel file');
    } finally {
      setIsUploading(false);
    }
  };

  // Step 3: Apply Changes
  const handleApplyChanges = async () => {
    if (!previewReport || !previewReport.valid) return;
    
    setIsApplying(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/exercises/import/apply`, {
        preview_report: previewReport
      }, {
        headers: getAuthHeaders()
      });
      
      setApplyResult(response.data);
      if (response.data.success) {
        onRefresh?.();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to apply changes');
    } finally {
      setIsApplying(false);
    }
  };

  // Reset workflow
  const handleReset = () => {
    setActiveStep(1);
    setPreviewReport(null);
    setApplyResult(null);
    setError(null);
    setUploadedFileName(null);
  };

  return (
    <div className="space-y-6">
      {/* Step Indicators */}
      <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
        <StepIndicator step={1} title="Export" active={activeStep === 1} completed={activeStep > 1} />
        <ArrowRight className="w-5 h-5 text-gray-400" />
        <StepIndicator step={2} title="Upload & Preview" active={activeStep === 2} completed={activeStep > 2 && previewReport?.valid} />
        <ArrowRight className="w-5 h-5 text-gray-400" />
        <StepIndicator step={3} title="Apply Changes" active={activeStep === 3} completed={!!applyResult?.success} />
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step 1: Export */}
      <Card className={activeStep === 1 ? 'ring-2 ring-blue-500' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Step 1: Export Exercise Database
          </CardTitle>
          <CardDescription>
            Download an Excel file with all exercises. The file is designed for human editing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="text-sm text-gray-600">
              <p className="font-medium mb-2">The exported file contains:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><span className="text-blue-600 font-medium">Exercises sheet</span> - All exercises with editable columns</li>
                <li><span className="text-blue-600 font-medium">Instructions sheet</span> - Guide for editing</li>
              </ul>
            </div>
            <Button 
              onClick={handleExport} 
              disabled={isExporting}
              className="w-fit"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Excel...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Excel File
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Upload and Preview */}
      <Card className={activeStep === 2 ? 'ring-2 ring-blue-500' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Step 2: Upload Edited File & Preview Changes
          </CardTitle>
          <CardDescription>
            Upload your edited Excel file. Changes will be validated and shown for review before applying.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Excel File
                    </>
                  )}
                </div>
              </label>
              {uploadedFileName && (
                <span className="text-sm text-gray-600">
                  Uploaded: {uploadedFileName}
                </span>
              )}
            </div>

            {/* Preview Report */}
            {previewReport && (
              <div className="mt-4 space-y-4">
                {/* Validation Status */}
                <div className={`p-4 rounded-lg ${previewReport.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {previewReport.valid ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-medium ${previewReport.valid ? 'text-green-700' : 'text-red-700'}`}>
                      {previewReport.valid ? 'Validation Passed' : 'Validation Failed'}
                    </span>
                  </div>
                </div>

                {/* Errors */}
                {previewReport.errors?.length > 0 && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-medium text-red-700 mb-2">Errors ({previewReport.errors.length})</h4>
                    <ul className="text-sm text-red-600 space-y-1 max-h-32 overflow-y-auto">
                      {previewReport.errors.map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {previewReport.warnings?.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-medium text-yellow-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Warnings ({previewReport.warnings.length})
                    </h4>
                    <ul className="text-sm text-yellow-600 space-y-1 max-h-32 overflow-y-auto">
                      {previewReport.warnings.map((warn, idx) => (
                        <li key={idx}>• {warn}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-700">{previewReport.summary?.keep || 0}</div>
                    <div className="text-sm text-gray-500">Keep</div>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-700">{previewReport.summary?.update || 0}</div>
                    <div className="text-sm text-blue-600 flex items-center justify-center gap-1">
                      <Edit className="w-3 h-3" /> Update
                    </div>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-700">{previewReport.summary?.merge || 0}</div>
                    <div className="text-sm text-purple-600 flex items-center justify-center gap-1">
                      <GitMerge className="w-3 h-3" /> Merge
                    </div>
                  </div>
                  <div className="bg-red-100 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-700">{previewReport.summary?.delete || 0}</div>
                    <div className="text-sm text-red-600 flex items-center justify-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </div>
                  </div>
                </div>

                {/* Detailed Changes */}
                {previewReport.valid && (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Updates */}
                    {previewReport.updates?.length > 0 && (
                      <div className="border rounded-lg">
                        <div className="bg-blue-50 px-4 py-2 border-b flex items-center gap-2">
                          <Edit className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-blue-700">Updates ({previewReport.updates.length})</span>
                        </div>
                        <div className="divide-y max-h-48 overflow-y-auto">
                          {previewReport.updates.map((update, idx) => (
                            <div key={idx} className="p-3 text-sm">
                              <div className="font-medium">{update.exercise_name}</div>
                              <div className="text-gray-500 text-xs mb-1">ID: {update.exercise_id}</div>
                              <div className="space-y-1">
                                {update.changes.map((change, cidx) => (
                                  <div key={cidx} className="text-xs bg-gray-50 p-1 rounded">
                                    <span className="font-medium">{change.field}:</span>{' '}
                                    <span className="text-red-500 line-through">{typeof change.old === 'object' ? JSON.stringify(change.old) : change.old}</span>
                                    {' → '}
                                    <span className="text-green-600">{typeof change.new === 'object' ? JSON.stringify(change.new) : change.new}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Merges */}
                    {previewReport.merges?.length > 0 && (
                      <div className="border rounded-lg">
                        <div className="bg-purple-50 px-4 py-2 border-b flex items-center gap-2">
                          <GitMerge className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-purple-700">Merges ({previewReport.merges.length})</span>
                        </div>
                        <div className="divide-y max-h-48 overflow-y-auto">
                          {previewReport.merges.map((merge, idx) => (
                            <div key={idx} className="p-3 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-red-600">{merge.source_name}</span>
                                <ArrowRight className="w-4 h-4" />
                                <span className="font-medium text-green-600">{merge.target_name}</span>
                              </div>
                              {merge.notes && <div className="text-xs text-gray-500 mt-1">Note: {merge.notes}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Deletes */}
                    {previewReport.deletes?.length > 0 && (
                      <div className="border rounded-lg">
                        <div className="bg-red-50 px-4 py-2 border-b flex items-center gap-2">
                          <Trash2 className="w-4 h-4 text-red-600" />
                          <span className="font-medium text-red-700">Deletes ({previewReport.deletes.length})</span>
                        </div>
                        <div className="divide-y max-h-48 overflow-y-auto">
                          {previewReport.deletes.map((del, idx) => (
                            <div key={idx} className="p-3 text-sm">
                              <div className="font-medium text-red-600">{del.exercise_name}</div>
                              <div className="text-gray-500 text-xs">ID: {del.exercise_id}</div>
                              {del.notes && <div className="text-xs text-gray-500 mt-1">Note: {del.notes}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Apply Changes */}
      <Card className={activeStep === 3 ? 'ring-2 ring-blue-500' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5" />
            Step 3: Apply Changes
          </CardTitle>
          <CardDescription>
            Review the changes above and click to apply them permanently.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {applyResult ? (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${applyResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {applyResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-medium ${applyResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {applyResult.success ? 'Changes Applied Successfully!' : 'Some errors occurred'}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-100 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-700">{applyResult.updates_applied}</div>
                  <div className="text-sm text-blue-600">Updated</div>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-700">{applyResult.merges_applied}</div>
                  <div className="text-sm text-purple-600">Merged</div>
                </div>
                <div className="bg-red-100 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-700">{applyResult.deletes_applied}</div>
                  <div className="text-sm text-red-600">Deleted</div>
                </div>
              </div>

              {applyResult.errors?.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-700 mb-2">Errors</h4>
                  <ul className="text-sm text-red-600 space-y-1">
                    {applyResult.errors.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button onClick={handleReset} variant="outline" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Start New Import
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {previewReport?.valid ? (
                <>
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      This action is <strong>permanent</strong>. Please review all changes above before proceeding.
                    </AlertDescription>
                  </Alert>
                  <div className="flex gap-4">
                    <Button 
                      onClick={handleApplyChanges} 
                      disabled={isApplying}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {isApplying ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Applying Changes...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirm & Apply Changes
                        </>
                      )}
                    </Button>
                    <Button onClick={handleReset} variant="outline">
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-sm">
                  Upload and validate an Excel file first to enable this step.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExcelWorkflow;
